import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { api } from "@/lib/api";

/**
 * Phase LOGIN-STABILITY Track 2 — surfaces the boot-time ffmpeg probe
 * so operators see immediately why the final-video stitch isn't running.
 *
 * Renders nothing when ffmpeg is available.  When unavailable, renders
 * a persistent destructive banner with the missing binaries + install
 * hint.  Auto-refreshes every 60s so reinstalls reflect without a page
 * reload.
 */
export default function FfmpegStatusBanner() {
  const { data } = useQuery({
    queryKey: ["system-ffmpeg-status"],
    queryFn: async () => {
      const res = await api.get("/system/ffmpeg-status");
      return res.data;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!data || data.available !== false) return null;
  const missing = Array.isArray(data.missing_binaries) ? data.missing_binaries : [];

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
      data-testid="ffmpeg-status-banner"
      role="alert"
    >
      <AlertTriangle size={18} className="mt-0.5 shrink-0 text-rose-600" />
      <div className="flex-1">
        <div className="font-medium" data-testid="ffmpeg-status-banner-title">
          Video stitching unavailable — ffmpeg/ffprobe missing on the backend host.
        </div>
        <div className="mt-1 text-rose-800" data-testid="ffmpeg-status-banner-body">
          {missing.length > 0 && (
            <>Missing binaries: <code className="font-mono">{missing.join(", ")}</code>. </>
          )}
          Contact admin to run <code className="font-mono">apt-get install -y ffmpeg</code> on the
          backend container, then restart the backend supervisor process.
        </div>
      </div>
    </div>
  );
}
